import { Check, Send, UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRfpQuestionDiscussion } from "../../hooks/useRfpQuestionDiscussion";
import type { RfpAssignableUser } from "../../types/rfp";
import { Drawer } from "./Drawer";

type DiscussionReviewer = {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly initials: string;
};

const getReviewerInitials = (name: string, email: string): string => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length > 0) {
    return words
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .join("");
  }

  const emailName = email.split("@")[0]?.trim() ?? "";
  if (!emailName) {
    return "?";
  }
  return emailName.slice(0, 2).toUpperCase();
};

type ReviewerListItemProps = {
  readonly reviewer: DiscussionReviewer;
  readonly isSelected: boolean;
  readonly disabled: boolean;
  readonly onToggle: (reviewerId: string) => void;
};

const ReviewerListItem = ({ reviewer, isSelected, disabled, onToggle }: ReviewerListItemProps) => (
  <button
    type="button"
    onClick={() => onToggle(reviewer.id)}
    disabled={disabled}
    className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70"
  >
    <div
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected ? "border-primary-500 bg-primary-500" : "border-gray-300 bg-white"}`}
    >
      {isSelected ? <Check className="h-3 w-3 text-white" /> : null}
    </div>
    <div
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[10px] font-semibold text-white"
    >
      {reviewer.initials}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-medium text-gray-800">{reviewer.name}</p>
      <p className="truncate text-[10px] text-gray-500">{reviewer.email}</p>
    </div>
  </button>
);

type SelectedReviewerChipProps = {
  readonly reviewer: DiscussionReviewer;
  readonly disabled: boolean;
  readonly onRemove: (reviewerId: string) => void;
};

const SelectedReviewerChip = ({ reviewer, disabled, onRemove }: SelectedReviewerChipProps) => (
  <div className="inline-flex max-w-full items-center gap-2 rounded-md bg-slate-100 px-2.5 py-1.5">
    <div
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[9px] font-semibold text-white"
    >
      {reviewer.initials}
    </div>
    <span className="truncate text-xs font-medium text-gray-800">{reviewer.name}</span>
    <button
      type="button"
      onClick={() => onRemove(reviewer.id)}
      disabled={disabled}
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-gray-500 transition-colors hover:bg-slate-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
      aria-label={`Remove ${reviewer.name}`}
    >
      <X className="h-3 w-3" />
    </button>
  </div>
);

type DiscussionComposerProps = {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSend: () => void;
  readonly disabled: boolean;
  readonly isSending: boolean;
};

const DiscussionComposer = ({
  value,
  onChange,
  onSend,
  disabled,
  isSending,
}: DiscussionComposerProps) => {
  const canSend = value.trim().length > 0 && !disabled && !isSending;

  return (
    <div className="border-t border-gray-200 p-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Type your message..."
          disabled={disabled || isSending}
          className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none transition-colors focus:border-primary-400"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          className={`inline-flex h-10 w-10 items-center justify-center rounded transition-colors ${
            canSend ? "bg-primary-500 text-white hover:bg-primary-600" : "cursor-not-allowed bg-gray-200 text-gray-400"
          }`}
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

type RfpDiscussionDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly rfpId: string | null;
  readonly questionId: string | null;
  readonly contextLabel: string;
  readonly users: readonly RfpAssignableUser[];
};

type LoadingSpinnerProps = {
  readonly className?: string;
};

const LoadingSpinner = ({ className = "h-8 w-8" }: LoadingSpinnerProps) => (
  <svg className={`${className} animate-spin text-primary-500`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const formatCommentDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const RfpDiscussionDrawer = ({
  isOpen,
  onClose,
  rfpId,
  questionId,
  contextLabel,
  users,
}: RfpDiscussionDrawerProps) => {
  const [isReviewerMenuOpen, setIsReviewerMenuOpen] = useState(false);
  const [selectedReviewerIds, setSelectedReviewerIds] = useState<string[]>([]);
  const [draftMessage, setDraftMessage] = useState("");
  const [pendingReviewerIds, setPendingReviewerIds] = useState<string[]>([]);
  const normalizedRfpId = rfpId?.trim() ?? "";
  const normalizedQuestionId = questionId?.trim() ?? "";
  const drawerContextKey = `${normalizedRfpId}:${normalizedQuestionId}`;
  const activeDrawerContextRef = useRef(drawerContextKey);
  const {
    discussion,
    loading,
    error,
    isAddingReviewer,
    isRemovingReviewer,
    isPostingComment,
    loadDiscussion,
    addReviewer,
    removeReviewer,
    addComment,
  } = useRfpQuestionDiscussion({
    rfpId,
    questionId,
    isOpen,
  });
  const reviewers = useMemo<readonly DiscussionReviewer[]>(
    () =>
      users.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        initials: getReviewerInitials(user.name, user.email),
      })),
    [users],
  );

  useEffect(() => {
    activeDrawerContextRef.current = isOpen ? drawerContextKey : "";
  }, [drawerContextKey, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedReviewerIds([]);
      setPendingReviewerIds([]);
      setDraftMessage("");
      setIsReviewerMenuOpen(false);
      return;
    }

    setSelectedReviewerIds([]);
    setPendingReviewerIds([]);
    setDraftMessage("");
    setIsReviewerMenuOpen(false);
  }, [drawerContextKey, isOpen]);

  useEffect(() => {
    const reviewerIdSet = new Set(reviewers.map((reviewer) => reviewer.id));
    setSelectedReviewerIds((current) => current.filter((id) => reviewerIdSet.has(id)));
  }, [reviewers]);

  useEffect(() => {
    if (!discussion) {
      return;
    }

    setSelectedReviewerIds(discussion.reviewers.map((reviewer) => reviewer.user_id));
  }, [discussion]);

  const selectedReviewersCount = selectedReviewerIds.length;
  const reviewerButtonLabel = useMemo(() => {
    if (selectedReviewersCount === 0) return "Add Reviewer";
    return selectedReviewersCount === 1 ? "1 Reviewer Selected" : `${selectedReviewersCount} Reviewers Selected`;
  }, [selectedReviewersCount]);
  const selectedReviewers = useMemo(
    () => reviewers.filter((reviewer) => selectedReviewerIds.includes(reviewer.id)),
    [reviewers, selectedReviewerIds],
  );
  const isReviewerBusy =
    isAddingReviewer || isRemovingReviewer || (loading && pendingReviewerIds.length > 0);
  const hasComments = Boolean(discussion?.comments.length);
  const showChatOverlay = loading && hasComments;
  const showInitialChatLoader = loading && !hasComments;

  const handleAddReviewer = (reviewerId: string) => {
    if (pendingReviewerIds.includes(reviewerId)) {
      return;
    }

    const requestContextKey = drawerContextKey;
    setSelectedReviewerIds((current) => [...current, reviewerId]);
    setPendingReviewerIds((current) => [...current, reviewerId]);
    void (async () => {
      const success = await addReviewer(reviewerId);
      if (activeDrawerContextRef.current !== requestContextKey) {
        return;
      }
      setPendingReviewerIds((current) => current.filter((id) => id !== reviewerId));
      if (!success) {
        setSelectedReviewerIds((current) => current.filter((id) => id !== reviewerId));
      }
    })();
  };
  const handleRemoveReviewer = (reviewerId: string) => {
    if (!selectedReviewerIds.includes(reviewerId) || pendingReviewerIds.includes(reviewerId)) {
      return;
    }

    const requestContextKey = drawerContextKey;
    setSelectedReviewerIds((current) => current.filter((id) => id !== reviewerId));
    setPendingReviewerIds((current) => [...current, reviewerId]);
    void (async () => {
      const success = await removeReviewer(reviewerId);
      if (activeDrawerContextRef.current !== requestContextKey) {
        return;
      }
      setPendingReviewerIds((current) => current.filter((id) => id !== reviewerId));
      if (!success) {
        setSelectedReviewerIds((current) => [...current, reviewerId]);
      }
    })();
  };
  const handleToggleReviewer = (reviewerId: string) => {
    if (selectedReviewerIds.includes(reviewerId)) {
      handleRemoveReviewer(reviewerId);
      return;
    }

    handleAddReviewer(reviewerId);
  };
  const handleSendComment = () => {
    const nextComment = draftMessage.trim();
    if (!nextComment || isAddingReviewer || isPostingComment) {
      return;
    }

    void (async () => {
      const success = await addComment(nextComment);
      if (success) {
        setDraftMessage("");
      }
    })();
  };
  const isSendDisabled = isAddingReviewer || !questionId || !rfpId;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Discussion" className="flex flex-1 flex-col p-0">
      <div className="border-b border-gray-200 px-4 py-3">
        <p className="text-xs text-gray-500">{contextLabel}</p>
        {selectedReviewers.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedReviewers.map((reviewer) => (
              <SelectedReviewerChip
                key={reviewer.id}
                reviewer={reviewer}
                disabled={isAddingReviewer || isRemovingReviewer || pendingReviewerIds.includes(reviewer.id)}
                onRemove={handleRemoveReviewer}
              />
            ))}
          </div>
        ) : null}
        {isReviewerBusy ? (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-slate-100 px-2.5 py-1 text-[11px] text-slate-700">
            <LoadingSpinner className="h-3.5 w-3.5" />
            Updating reviewers...
          </div>
        ) : null}
        <div className="relative mt-3">
          <button
            type="button"
            onClick={() => setIsReviewerMenuOpen((value) => !value)}
            disabled={!questionId || !rfpId}
            className="inline-flex w-full items-center justify-center gap-2 rounded border border-primary-500 bg-slate-50 px-3 py-2 text-xs font-medium text-primary-600"
          >
            <UserPlus className="h-3.5 w-3.5" />
            {reviewerButtonLabel}
          </button>
          {isReviewerMenuOpen ? (
            <div className="absolute left-0 right-0 top-full z-10 mt-2 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {reviewers.length === 0 ? (
                <div className="px-3 py-4 text-xs text-gray-500">No users available.</div>
              ) : (
                reviewers.map((reviewer) => (
                  <ReviewerListItem
                    key={reviewer.id}
                    reviewer={reviewer}
                    isSelected={selectedReviewerIds.includes(reviewer.id)}
                    disabled={
                      isAddingReviewer || isRemovingReviewer || pendingReviewerIds.includes(reviewer.id)
                    }
                    onToggle={handleToggleReviewer}
                  />
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto p-4">
        {error ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}
        {showInitialChatLoader ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-sm text-gray-500">
            <LoadingSpinner />
            <p>Loading discussion...</p>
          </div>
        ) : hasComments ? (
          <div className="relative flex-1">
            <div className="space-y-3">
              {discussion?.comments.map((comment) => (
                <article key={comment.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-800">{comment.user_name}</p>
                    <div className="flex items-center gap-2">
                      {comment.variant_type ? (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-700">
                          {comment.variant_type}
                        </span>
                      ) : null}
                      <span className="text-[10px] text-gray-500">{formatCommentDate(comment.created_at)}</span>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-700">{comment.comment_text}</p>
                </article>
              ))}
            </div>
            {showChatOverlay ? (
              <div className="absolute inset-0 flex items-center justify-center rounded-md bg-white/60 backdrop-blur-[1px]">
                <div className="inline-flex items-center gap-2 rounded-md bg-white/90 px-3 py-1.5 text-xs text-gray-700 shadow-sm">
                  <LoadingSpinner className="h-4 w-4" />
                  Updating discussion...
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center text-center">
            <p className="text-sm text-gray-500">
              No messages yet.
              <br />
              Start the discussion!
            </p>
          </div>
        )}
        {error ? (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => void loadDiscussion()}
              className="rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700"
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>

      <DiscussionComposer
        value={draftMessage}
        onChange={setDraftMessage}
        onSend={handleSendComment}
        disabled={isSendDisabled}
        isSending={isPostingComment}
      />
    </Drawer>
  );
};
